import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  AssistantApiError,
  AssistantSessionStreamHandlers,
  AssistantOutcome,
  AssistantStreamUnavailableError,
  ChatMessageView,
  ChatRequestStatus,
  ChatRole,
  ClarificationSuggestion,
  EvidenceCitation,
} from '@api/assistant/assistantApi';
import useAssistantApi from '@hooks/useAssistantApi';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser';
import { ToastContext } from '@components/auth/ToastContext';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { createLogger } from '@utils/logger';
import { generateUUID } from '@utils/utils.tsx';
import { safeBuildPageContext } from '@components/ringil3/Features/Assistant/pageContext/snapshot';

const log = createLogger('useAssistantChat');

/** Messages load 20 at a time; scrolling to the top of the list pulls the next (older) page. */
const HISTORY_PAGE_SIZE = 20;

/**
 * Answers are followed over the SSE stream; polling is the fallback (see followRequest). Polling runs
 * fast at first, slower for long answers.
 */
const POLL_FAST_MS = 1500;
const POLL_SLOW_MS = 3000;
const POLL_SLOWDOWN_AFTER_MS = 15_000;
const POLL_TIMEOUT_MS = 120_000;
/** Transient poll failures are tolerated; this many in a row ends the request. */
const MAX_CONSECUTIVE_POLL_ERRORS = 3;
/** A stream silent for this long — no event, no keep-alive — is given up on in favour of polling. */
const STREAM_IDLE_TIMEOUT_MS = 20_000;
/** How long a reconnect to the session stream waits, growing with each failure in a row. */
const SESSION_RECONNECT_DELAYS_MS = [1_000, 2_000, 5_000, 10_000];
/** Responses that mean "this server has no stream", as opposed to a one-off failure worth retrying next time. */
const STREAM_UNSUPPORTED_STATUSES = [404, 405, 406, 501];

/**
 * Cleared, for the rest of the page's life, once the server shows it cannot stream — so every later
 * question goes straight to polling instead of paying for a failed attempt first. Tracked per endpoint:
 * a backend may well have one and not the other.
 */
let streamingAvailable = true;
let sessionStreamingAvailable = true;

export interface AssistantChatMessage {
  /** Local-only React key. History messages get a deterministic one so re-renders stay stable. */
  id: string;
  role: ChatRole;
  content: string;
  createdAt: string;
  citations?: EvidenceCitation[];
  /** Choices offered by a CLARIFICATION answer — picking one sends its text as the next question. */
  suggestions?: ClarificationSuggestion[];
  outcome?: AssistantOutcome;
  /** Local lifecycle of an in-flight answer; absent for settled messages. */
  state?: 'pending' | 'failed';
  /**
   * While pending: the pipeline stages the stream has reported so far, in order (raw step ids such as
   * `retrieve_evidence` — the bubble turns them into labels). Meanwhile `content` fills with the
   * answer's text as it streams in.
   */
  progress?: string[];
  /** The question this answer belongs to, so a failed answer can offer a retry. */
  retryText?: string;
}

const sessionStorageKey = (userId?: string) => `ringil.assistant.session.${userId ?? 'anonymous'}`;

const readStoredSession = (userId?: string): string | null => {
  try {
    return localStorage.getItem(sessionStorageKey(userId));
  } catch {
    return null;
  }
};

const writeStoredSession = (userId: string | undefined, sessionId: string | null) => {
  try {
    if (sessionId) localStorage.setItem(sessionStorageKey(userId), sessionId);
    else localStorage.removeItem(sessionStorageKey(userId));
  } catch {
    // Private-mode browsers can throw; the conversation still works, it just won't survive a reload.
  }
};

/**
 * `recent_messages` comes back newest-first (page 1 is the tail of the conversation), but the chat
 * renders oldest-first. Sorting on `created_at` rather than reversing keeps us correct even if the
 * backend ever flips its ordering.
 */
const toChatMessages = (page: ChatMessageView[]): AssistantChatMessage[] =>
  [...(page ?? [])]
    .sort((a, b) => a.created_at.localeCompare(b.created_at))
    .map((m, index) => ({
      id: `history-${m.created_at}-${index}`,
      role: m.role,
      content: m.content,
      createdAt: m.created_at,
      citations: m.citations,
      suggestions: m.suggestions,
    }));

/**
 * Owns one assistant conversation: history paging, message submission, and following each request to
 * its answer — over SSE, falling back to polling.
 */
export const useAssistantChat = () => {
  const api = useAssistantApi();
  const { tr } = useTranslationLgs();
  const { showToastMessage } = useContext(ToastContext);
  const { loggedUser } = useContext(AuthenticatedUserContext);
  const userId = loggedUser?.id;

  const [sessionId, setSessionId] = useState<string | null>(null);
  /** Loaded from `GET /chat/sessions/{id}`, oldest-first. */
  const [historyMessages, setHistoryMessages] = useState<AssistantChatMessage[]>([]);
  /** Messages produced in this panel session (optimistic user turns + their answers). */
  const [localMessages, setLocalMessages] = useState<AssistantChatMessage[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [sending, setSending] = useState(false);

  const loadedPages = useRef(0);
  const pollTimeoutRef = useRef<number | null>(null);
  const streamAbortRef = useRef<AbortController | null>(null);
  /** Watches over a request the session stream is carrying: if it goes unmentioned, polling takes over. */
  const watchdogRef = useRef<number | null>(null);
  /** The placeholder the in-flight request writes into — session stream events have to find it. */
  const pendingPlaceholderRef = useRef<string | null>(null);
  /** The open session stream: its connection, the session it follows, and whether it is attached. */
  const sessionStreamRef = useRef<{ controller: AbortController; sessionId: string; attached: boolean } | null>(null);
  const sessionReconnectRef = useRef<{ timer: number | null; failures: number }>({ timer: null, failures: 0 });
  /** Set while the panel is open: a closed panel does not hold a connection open. */
  const wantSessionStreamRef = useRef(false);
  const pendingRequestIdRef = useRef<string | null>(null);
  /** Guards every async completion so nothing writes state after unmount. */
  const aliveRef = useRef(true);

  const messages = useMemo(() => [...historyMessages, ...localMessages], [historyMessages, localMessages]);
  const hasMoreHistory = historyMessages.length < historyTotal;
  const hasConversation = sessionId !== null || messages.length > 0;

  /** Stops following the in-flight request, whichever way — stream or poll. */
  const clearPoll = useCallback(() => {
    if (pollTimeoutRef.current !== null) {
      window.clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    streamAbortRef.current?.abort();
    streamAbortRef.current = null;
    if (watchdogRef.current !== null) {
      window.clearTimeout(watchdogRef.current);
      watchdogRef.current = null;
    }
  }, []);

  /** Read at unmount, when the callback identity of the moment is not available any more. */
  const stopSessionStreamRef = useRef<() => void>(() => {});

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      clearPoll();
      stopSessionStreamRef.current();
    };
  }, [clearPoll]);

  const settlePlaceholder = useCallback((placeholderId: string, patch: Partial<AssistantChatMessage>) => {
    setLocalMessages(prev => prev.map(m => (m.id === placeholderId ? { ...m, ...patch } : m)));
  }, []);

  /** For streamed updates that build on the placeholder's current value (appended text, a new stage). */
  const updatePlaceholder = useCallback((placeholderId: string, update: (m: AssistantChatMessage) => Partial<AssistantChatMessage>) => {
    setLocalMessages(prev => prev.map(m => (m.id === placeholderId && m.state === 'pending' ? { ...m, ...update(m) } : m)));
  }, []);

  const failPlaceholder = useCallback(
    (placeholderId: string, detail?: string) => {
      settlePlaceholder(placeholderId, {
        state: 'failed',
        progress: undefined,
        content: detail || tr('Assistant.genericError', 'The assistant could not answer. Please try again.'),
      });
      pendingRequestIdRef.current = null;
      pendingPlaceholderRef.current = null;
      setSending(false);
    },
    [settlePlaceholder, tr],
  );

  /**
   * Applies a request status — from the stream or a poll, they are the same payload — to the answer
   * placeholder. Returns whether the request is finished.
   */
  const applyStatus = useCallback(
    (status: ChatRequestStatus, placeholderId: string): boolean => {
      if (status.status === 'SUCCEEDED') {
        settlePlaceholder(placeholderId, {
          state: undefined,
          progress: undefined,
          // The final text replaces whatever streamed in — it is the authoritative copy.
          content: status.answer?.content_markdown ?? tr('Assistant.emptyAnswer', 'The assistant returned an empty answer.'),
          citations: status.answer?.citations,
          suggestions: status.answer?.suggestions,
          outcome: status.answer?.outcome,
          createdAt: status.completed_at ?? status.updated_at,
        });
        pendingRequestIdRef.current = null;
        pendingPlaceholderRef.current = null;
        setSending(false);
        return true;
      }

      if (status.status === 'FAILED' || status.status === 'CANCELLED') {
        failPlaceholder(placeholderId, status.error_message);
        return true;
      }

      return false;
    },
    [failPlaceholder, settlePlaceholder, tr],
  );

  const pollRequest = useCallback(
    (requestId: string, placeholderId: string, startedAt: number, consecutiveErrors: number) => {
      const elapsed = Date.now() - startedAt;
      const delay = elapsed > POLL_SLOWDOWN_AFTER_MS ? POLL_SLOW_MS : POLL_FAST_MS;

      pollTimeoutRef.current = window.setTimeout(() => {
        api
          .getRequestStatus(requestId)
          .then(status => {
            if (!aliveRef.current || pendingRequestIdRef.current !== requestId) return;
            if (applyStatus(status, placeholderId)) return;

            if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
              log.debug('Assistant request timed out', { requestId });
              failPlaceholder(placeholderId, tr('Assistant.timeoutError', 'The assistant is taking too long to answer. Please try again.'));
              return;
            }

            pollRequest(requestId, placeholderId, startedAt, 0);
          })
          .catch(error => {
            if (!aliveRef.current || pendingRequestIdRef.current !== requestId) return;

            const errors = consecutiveErrors + 1;
            log.debug('Assistant poll failed', { requestId, errors, error });
            if (errors >= MAX_CONSECUTIVE_POLL_ERRORS) {
              failPlaceholder(placeholderId, error instanceof AssistantApiError ? undefined : error?.message);
              return;
            }
            pollRequest(requestId, placeholderId, startedAt, errors);
          });
      }, delay);
    },
    [api, applyStatus, failPlaceholder, tr],
  );

  /**
   * The stream events of the request currently being waited on. Events that name a different request —
   * another tab asking in the same session — are not ours to apply.
   */
  const isOurs = useCallback((requestId?: string) => !requestId || requestId === pendingRequestIdRef.current, []);

  const handleStep = useCallback(
    (step: string, requestId?: string) => {
      const placeholderId = pendingPlaceholderRef.current;
      if (!placeholderId || !isOurs(requestId)) return;
      updatePlaceholder(placeholderId, m => (m.progress?.[m.progress.length - 1] === step ? {} : { progress: [...(m.progress ?? []), step] }));
    },
    [isOurs, updatePlaceholder],
  );

  const handleToken = useCallback(
    (text: string, requestId?: string) => {
      const placeholderId = pendingPlaceholderRef.current;
      if (!placeholderId || !isOurs(requestId)) return;
      updatePlaceholder(placeholderId, m => ({ content: m.content + text }));
    },
    [isOurs, updatePlaceholder],
  );

  /** Returns whether the request this status belongs to is now finished. */
  const handleStatus = useCallback(
    (status: ChatRequestStatus) => {
      const placeholderId = pendingPlaceholderRef.current;
      if (!placeholderId || !isOurs(status.request_id)) return false;
      return applyStatus(status, placeholderId);
    },
    [applyStatus, isOurs],
  );

  /**
   * Polls the request currently in flight — the way back from anything the stream cannot deliver: it
   * broke, went quiet, or we attached to the session while the request was already running, in which
   * case its events are gone (the session stream does not replay).
   */
  const resyncPendingRequest = useCallback(
    (reason: string) => {
      const requestId = pendingRequestIdRef.current;
      const placeholderId = pendingPlaceholderRef.current;
      if (!requestId || !placeholderId || pollTimeoutRef.current !== null) return;
      log.debug('Assistant falling back to polling', { requestId, reason });
      pollRequest(requestId, placeholderId, Date.now(), 0);
    },
    [pollRequest],
  );

  const stopSessionStream = useCallback(() => {
    if (sessionReconnectRef.current.timer !== null) {
      window.clearTimeout(sessionReconnectRef.current.timer);
      sessionReconnectRef.current.timer = null;
    }
    sessionStreamRef.current?.controller.abort();
    sessionStreamRef.current = null;
  }, []);

  /**
   * Holds one stream open for the whole conversation: every request's steps, tokens and final status
   * arrive on it, so a question asked while it is attached needs no stream of its own.
   *
   * Reconnects itself when the server closes it or the connection drops — which, unlike the per-request
   * stream, leaves the running request alone (cancelling is `cancelRequest`). Events published while
   * nobody was attached are gone, so an attach that finds one of our requests in flight reads its answer
   * back by polling instead.
   */
  stopSessionStreamRef.current = stopSessionStream;

  const connectSessionStream = useCallback(
    (targetSessionId: string) => {
      if (!sessionStreamingAvailable || typeof ReadableStream === 'undefined' || !wantSessionStreamRef.current) return;
      const open = sessionStreamRef.current;
      if (open && open.sessionId === targetSessionId && !open.controller.signal.aborted) return;
      stopSessionStream();

      const controller = new AbortController();
      const connection = { controller, sessionId: targetSessionId, attached: false };
      sessionStreamRef.current = connection;
      let idleTimer: number | undefined;
      const mine = () => aliveRef.current && sessionStreamRef.current === connection && !controller.signal.aborted;
      const resetIdle = () => {
        window.clearTimeout(idleTimer);
        // A stream that has stopped even sending keep-alives is dead; drop it and reconnect.
        idleTimer = window.setTimeout(() => controller.abort(), STREAM_IDLE_TIMEOUT_MS * 3);
      };
      resetIdle();

      const handlers: AssistantSessionStreamHandlers = {
        onAlive: resetIdle,
        // The connection being live is what makes it usable — not the arrival of a particular event.
        // Waiting for `snapshot` would leave the stream unused, and every question opening one of its
        // own, if the server ever names that event differently or sends none.
        onOpen: () => {
          if (!mine()) return;
          connection.attached = true;
          sessionReconnectRef.current.failures = 0;
          log.debug('Assistant session stream open', { sessionId: targetSessionId });
          // Attached mid-request: its events were published to nobody and are not replayed.
          if (pendingRequestIdRef.current) resyncPendingRequest('attached while a request was running');
        },
        onAttached: snapshot => {
          if (mine()) log.debug('Assistant session stream snapshot', { sessionId: targetSessionId, snapshot });
        },
        onStep: handleStep,
        onToken: handleToken,
        onStatus: status => {
          if (mine()) handleStatus(status);
        },
        onRequestError: (requestId, data) => {
          if (!mine() || !isOurs(requestId)) return;
          log.debug('Assistant session stream reported an error for the pending request', { requestId, data });
          resyncPendingRequest('error event on the session stream');
        },
      };

      api
        .streamSession(targetSessionId, handlers, controller.signal)
        .catch(error => {
          if (error instanceof AssistantStreamUnavailableError) {
            if (error.status < 300 || STREAM_UNSUPPORTED_STATUSES.includes(error.status)) sessionStreamingAvailable = false;
            log.debug('Assistant session stream unavailable', { sessionId: targetSessionId, status: error.status, contentType: error.contentType });
          } else if (!controller.signal.aborted) {
            log.debug('Assistant session stream failed', { sessionId: targetSessionId, error });
          }
        })
        .finally(() => {
          window.clearTimeout(idleTimer);
          if (sessionStreamRef.current !== connection) return;
          sessionStreamRef.current = null;
          // The request keeps running without us, so anything in flight now needs polling.
          if (pendingRequestIdRef.current) resyncPendingRequest('session stream closed');
          if (!aliveRef.current || !wantSessionStreamRef.current || !sessionStreamingAvailable) return;

          const { failures } = sessionReconnectRef.current;
          const delay = SESSION_RECONNECT_DELAYS_MS[Math.min(failures, SESSION_RECONNECT_DELAYS_MS.length - 1)];
          sessionReconnectRef.current.failures = failures + 1;
          sessionReconnectRef.current.timer = window.setTimeout(() => {
            sessionReconnectRef.current.timer = null;
            if (aliveRef.current && wantSessionStreamRef.current) connectSessionStream(targetSessionId);
          }, delay);
        });
    },
    [api, handleStatus, handleStep, handleToken, isOurs, resyncPendingRequest, stopSessionStream],
  );

  /**
   * Follows a submitted request to its answer. In order of preference: the session stream, if one is
   * already attached to this session — then the request needs nothing of its own; otherwise a stream for
   * this request alone; otherwise polling, which is also the way back from any of the above going wrong.
   * Nothing is lost by trying: they all carry the same statuses.
   */
  const followRequest = useCallback(
    (requestId: string, placeholderId: string, streamedSessionId: string) => {
      const startedAt = Date.now();
      const session = sessionStreamRef.current;
      const onSessionStream = !!session?.attached && session.sessionId === streamedSessionId && !session.controller.signal.aborted;
      log.debug('Assistant following request', { requestId, via: onSessionStream ? 'session stream' : streamingAvailable ? 'request stream' : 'polling' });
      if (onSessionStream) {
        // Watchdog only: the session stream is carrying this request, but if it says nothing about it
        // for long enough, polling takes over while the stream stays up for whatever comes next.
        watchdogRef.current = window.setTimeout(() => resyncPendingRequest('session stream said nothing about the request'), STREAM_IDLE_TIMEOUT_MS);
        return;
      }

      if (!streamingAvailable || typeof ReadableStream === 'undefined') {
        pollRequest(requestId, placeholderId, startedAt, 0);
        return;
      }

      const controller = new AbortController();
      streamAbortRef.current = controller;
      let finished = false;
      let idleTimer: number | undefined;
      const stillFollowing = () => aliveRef.current && pendingRequestIdRef.current === requestId && !controller.signal.aborted;
      const resetIdle = () => {
        window.clearTimeout(idleTimer);
        idleTimer = window.setTimeout(() => {
          log.debug('Assistant stream went quiet, falling back to polling', { requestId });
          controller.abort();
        }, STREAM_IDLE_TIMEOUT_MS);
      };
      resetIdle();

      api
        .streamRequest(
          requestId,
          {
            onAlive: resetIdle,
            onStep: step => {
              if (stillFollowing()) handleStep(step);
            },
            onToken: text => {
              if (stillFollowing()) handleToken(text);
            },
            onStatus: status => {
              if (!stillFollowing()) return;
              if (handleStatus(status)) {
                finished = true;
                controller.abort();
              }
            },
          },
          controller.signal,
        )
        .catch(error => {
          if (error instanceof AssistantStreamUnavailableError) {
            // A 200 that is not an event stream, or a status meaning "no such route" — this server does
            // not stream; anything else (401, 5xx) may be a one-off, so the next question tries again.
            if (error.status < 300 || STREAM_UNSUPPORTED_STATUSES.includes(error.status)) streamingAvailable = false;
            log.debug('Assistant stream unavailable, falling back to polling', { requestId, status: error.status, contentType: error.contentType });
          } else if (!controller.signal.aborted) {
            // Includes an `error` event without a final status: polling finds out how the request ended.
            log.debug('Assistant stream failed, falling back to polling', { requestId, error });
          }
        })
        .finally(() => {
          window.clearTimeout(idleTimer);
          if (streamAbortRef.current === controller) streamAbortRef.current = null;
          // Cancelled, answered, or unmounted — nothing left to follow. Otherwise the stream ended or
          // broke before the request did: polling takes over from here.
          if (finished || !aliveRef.current || pendingRequestIdRef.current !== requestId) return;
          pollRequest(requestId, placeholderId, startedAt, 0);
        });
    },
    [api, handleStatus, handleStep, handleToken, pollRequest, resyncPendingRequest],
  );

  const loadHistoryPage = useCallback(
    (targetSessionId: string, pageNumber: number) => {
      setLoadingHistory(true);
      api
        .getSession(targetSessionId, pageNumber, HISTORY_PAGE_SIZE)
        .then(session => {
          if (!aliveRef.current) return;
          const page = toChatMessages(session.recent_messages?.data);
          // Higher page numbers are older, so they go in front of what is already loaded.
          setHistoryMessages(prev => (pageNumber === 1 ? page : [...page, ...prev]));
          setHistoryTotal(session.recent_messages?.total ?? 0);
          loadedPages.current = pageNumber;
          setLoadingHistory(false);
        })
        .catch(error => {
          if (!aliveRef.current) return;
          setLoadingHistory(false);
          log.debug('Failed to load assistant session', { targetSessionId, pageNumber, error });
          // A stored session id can be stale (archived, or wiped backend-side on alpha) — start fresh
          // rather than trapping the user on an error screen.
          if (error instanceof AssistantApiError && error.status === 404) {
            setSessionId(null);
            writeStoredSession(userId, null);
            return;
          }
          showToastMessage(tr('Assistant.title', 'How to'), tr('Assistant.historyError', 'The conversation could not be loaded.'), 'error');
        });
    },
    [api, showToastMessage, tr, userId],
  );

  /** Called when the panel opens: resumes the stored conversation, if there is one, and follows it. */
  const openConversation = useCallback(() => {
    const stored = readStoredSession(userId);
    wantSessionStreamRef.current = true;
    if (stored) connectSessionStream(stored);
    setSessionId(stored);
    setHistoryMessages([]);
    setLocalMessages([]);
    setHistoryTotal(0);
    loadedPages.current = 0;
    if (stored) loadHistoryPage(stored, 1);
  }, [connectSessionStream, loadHistoryPage, userId]);

  /**
   * Called when the panel closes: lets go of the session stream, since a closed panel has no use for
   * one. A request still running is unaffected — the answer is read back when the panel reopens.
   */
  const closeConversation = useCallback(() => {
    wantSessionStreamRef.current = false;
    stopSessionStream();
  }, [stopSessionStream]);

  const loadOlderMessages = useCallback(() => {
    if (!sessionId || loadingHistory || !hasMoreHistory) return;
    loadHistoryPage(sessionId, loadedPages.current + 1);
  }, [hasMoreHistory, loadHistoryPage, loadingHistory, sessionId]);

  const cancelPendingRequest = useCallback(() => {
    const requestId = pendingRequestIdRef.current;
    clearPoll();
    pendingRequestIdRef.current = null;
    pendingPlaceholderRef.current = null;
    setSending(false);
    if (requestId) {
      api.cancelRequest(requestId).catch(error => log.debug('Cancelling assistant request failed', { requestId, error }));
    }
    return requestId;
  }, [api, clearPoll]);

  const sendMessage = useCallback(
    (text: string) => {
      const message = text.trim();
      if (!message || sending) return;

      const now = new Date().toISOString();
      const placeholderId = `local-answer-${generateUUID()}`;
      setLocalMessages(prev => [
        ...prev,
        { id: `local-question-${generateUUID()}`, role: 'USER', content: message, createdAt: now },
        { id: placeholderId, role: 'ASSISTANT', content: '', createdAt: now, state: 'pending', retryText: message },
      ]);
      setSending(true);

      // Taken now, when the user asks — a retry asks again, so it takes a new one.
      const page_context = safeBuildPageContext();

      api
        .submitRequest({ session_id: sessionId ?? undefined, message, ...(page_context && { page_context }) })
        .then(accepted => {
          if (!aliveRef.current) return;
          setSessionId(accepted.session_id);
          writeStoredSession(userId, accepted.session_id);
          pendingRequestIdRef.current = accepted.request_id;
          pendingPlaceholderRef.current = placeholderId;
          // Attaching now covers every later question in this session; this one is followed by whatever
          // is ready already, since events from before the attach are not replayed.
          connectSessionStream(accepted.session_id);
          followRequest(accepted.request_id, placeholderId, accepted.session_id);
        })
        .catch(error => {
          if (!aliveRef.current) return;
          log.debug('Submitting assistant request failed', { error });
          failPlaceholder(placeholderId, error instanceof AssistantApiError ? undefined : error?.message);
        });
    },
    [api, connectSessionStream, failPlaceholder, followRequest, sending, sessionId, userId],
  );

  const startNewConversation = useCallback(() => {
    cancelPendingRequest();
    // The stream follows a session; this one is being left behind.
    stopSessionStream();
    setSessionId(null);
    writeStoredSession(userId, null);
    setHistoryMessages([]);
    setLocalMessages([]);
    setHistoryTotal(0);
    loadedPages.current = 0;
  }, [cancelPendingRequest, stopSessionStream, userId]);

  return {
    messages,
    sessionId,
    hasConversation,
    hasMoreHistory,
    loadingHistory,
    sending,
    openConversation,
    closeConversation,
    loadOlderMessages,
    sendMessage,
    cancelPendingRequest,
    startNewConversation,
  };
};

export default useAssistantChat;
